// What the public home page says about Gallivant RP's evening at anchor
// (TOM-980). Every reading and sentence is derived here from her record
// (gallivant.ts) and Neo's status rules (sceneStatus.ts), so no number on the
// page is typed by hand.
import { ANCHORAGE_WATER_LABEL } from './anchorageLand';
import {
  FRESH_WATER_CAPACITY_GAL,
  clock,
  compassWord,
  day,
  fahrenheit,
  ft,
  gal,
  isLightWind,
} from './format';
import { GALLIVANT_AT, GALLIVANT_EVENING } from './gallivant';
import { sceneAnchor, sceneHeadline } from './sceneStatus';

const E = GALLIVANT_EVENING;

export const SCENE_BOAT = 'MV Gallivant';

/** Where she lies: "near Squirrel Cove". The name is the chart's water label. */
export const SCENE_ANCHORAGE = `near ${ANCHORAGE_WATER_LABEL.name}`;

/** How the anchor watch reads her distance at 7:20 PM. */
export const SCENE_ANCHOR = sceneAnchor(E.distanceFromAnchorM, E.anchorRadiusM);

/** Neo's headline for the scene, as the status rules write it. */
export const SCENE_HEADLINE = sceneHeadline(SCENE_ANCHOR, SCENE_BOAT);

/** The dated caption under the iPad. */
export const SCENE_CAPTION = `${SCENE_BOAT} at anchor ${SCENE_ANCHORAGE}, British Columbia, ${day(GALLIVANT_AT, true)}, ${clock(GALLIVANT_AT)}.`;

/** The iPad's text alternative. */
export const IPAD_LABEL = `The Neomaris app on an iPad: ${SCENE_BOAT} at anchor ${SCENE_ANCHORAGE}, with her chart, wind and readings, and Neo answering questions in a panel that slides in.`;

/** The readings the iPad shows, as text. */
export const SCENE_READINGS = {
  windKn: E.windSpeedKn.toFixed(0),
  /** "Light, from the south". */
  windWords: `${isLightWind(E.windSpeedKn) ? 'Light, from' : 'From'} the ${compassWord(E.windFromDeg)}`,
  windFromDeg: E.windFromDeg,
  depthFt: ft(E.depthM),
  toAnchorFt: ft(E.distanceFromAnchorM),
  /** Her distance from the anchor as a share of the circle, in whole percent. */
  toAnchorPct: Math.round((E.distanceFromAnchorM / E.anchorRadiusM) * 100),
  circleFt: ft(E.anchorRadiusM),
  pressureHpa: E.pressureHpa,
  airF: fahrenheit(E.airTempC),
  housePct: E.houseSocPct,
  houseV: E.houseVoltage.toFixed(1),
  waterGal: gal(E.freshWaterPct),
  waterCapacityGal: FRESH_WATER_CAPACITY_GAL,
  waterPct: Math.round(E.freshWaterPct),
};

/** Neo's answer to "Is the anchor holding?", by the anchor watch's reading. */
export function anchorAnswer(): string {
  const r = SCENE_READINGS;
  const where =
    SCENE_ANCHOR === 'holding'
      ? `Yes. Gallivant has stayed within ${ft(E.maxDistanceSinceWatchM)} ft of the anchor since ${clock(E.anchorWatchSince)}.`
      : SCENE_ANCHOR === 'drag_suspected'
        ? `It may be dragging. Gallivant is ${r.toAnchorFt} ft from the anchor, close to the edge of the ${r.circleFt} ft circle.`
        : `No. Gallivant is ${r.toAnchorFt} ft from the anchor, past the ${r.circleFt} ft circle.`;
  const wind = `The wind is ${isLightWind(E.windSpeedKn) ? 'light, ' : ''}${r.windKn} kn from the ${compassWord(E.windFromDeg)}, and the depth is ${r.depthFt} ft.`;
  return `${where} ${wind}`;
}
